import axios from "axios";
import { Crosshair, Gamepad2, Trophy, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";

// rotating weekly score target — deterministic per 7-day cycle
const WEEK_MS = 604800000;
const CYCLE_TARGETS = [2000, 2600, 3400, 4200];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RUNNER_BASELINE = 240;

const timeAgo = (iso) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "JUST NOW";
  if (s < 3600) return `${Math.floor(s / 60)}M AGO`;
  if (s < 86400) return `${Math.floor(s / 3600)}H AGO`;
  return `${Math.floor(s / 86400)}D AGO`;
};

export function GameAd() {
  const [best, setBest] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [runners, setRunners] = useState(null);
  const [highlights, setHighlights] = useState([]);

  useEffect(() => {
    setBest(Number(localStorage.getItem("wasteland-run-best") || 0));
    const ticker = window.setInterval(() => setNow(Date.now()), 30000);
    let active = true;
    axios
      .get(`${API}/enlist/count`)
      .then(({ data }) => {
        if (active && typeof data.count === "number") setRunners(RUNNER_BASELINE + data.count);
      })
      .catch(() => {});
    axios
      .get(`${API}/riotball/highlights`)
      .then(({ data }) => {
        if (active && Array.isArray(data.highlights)) setHighlights(data.highlights);
      })
      .catch(() => {});
    return () => {
      window.clearInterval(ticker);
      active = false;
    };
  }, []);

  const cycleTarget = CYCLE_TARGETS[Math.floor(now / WEEK_MS) % CYCLE_TARGETS.length];
  const resetIn = Math.max(0, (Math.floor(now / WEEK_MS) + 1) * WEEK_MS - now);
  const resetDays = Math.floor(resetIn / 86400000);
  const resetHours = Math.floor((resetIn % 86400000) / 3600000);

  return (
    <section className="game-ad" data-testid="game-ad-section">
      <Reveal className="game-ad-inner">
        <div className="game-ad-copy">
          <span>BLACK-OPS NET ARCADE // TRAINING SIM 07</span>
          <h2>The worm is loose. The wasteland is watching.</h2>
          <p>
            Dolo's Wasteland Run is live in the arcade vault — outrun four escalating worm classes, hoard the
            credits, and transmit your best signal to the mesh.
          </p>
          <p className="game-ad-cycle" data-testid="game-ad-cycle-target">
            <Crosshair size={13} />
            {best > 0 && best >= cycleTarget
              ? `CYCLE TARGET NEUTRALIZED — ${best} VS ${cycleTarget.toLocaleString()}. THE MESH SALUTES YOU.`
              : `WEEKLY WORM CHALLENGE — BEAT ${cycleTarget.toLocaleString()} THIS CYCLE.`}
          </p>
          <span className="game-ad-cycle-reset" data-testid="game-ad-cycle-reset">
            CYCLE RESETS IN {resetDays}D {String(resetHours).padStart(2, "0")}H
          </span>
          {runners !== null ? (
            <p className="game-ad-pulse" data-testid="game-ad-runner-count">
              <Users size={13} /> {runners.toLocaleString()} RUNNERS ENLISTED
            </p>
          ) : null}
          {best > 0 ? (
            <p className="game-ad-best" data-testid="game-ad-best-signal">
              <Trophy size={13} /> YOUR BEST SIGNAL: {best} — THE WORM THINKS YOU'VE GONE SOFT.
            </p>
          ) : null}
          <Link to="/wasteland-run" className="button button-primary" data-testid="home-game-ad-link">
            {best > 0 ? "RUN AGAIN" : "PLAY NOW"} <Gamepad2 size={16} />
          </Link>
        </div>
        <Link
          to="/wasteland-run"
          className="game-ad-art rb-glitch"
          data-testid="home-game-ad-art"
          aria-label="Play Dolo's Wasteland Run"
        >
          <img loading="lazy" src="/assets/game-cover.jpg" alt="Dolo's Wasteland Run — Dolo flees the mutated sand worm" />
          <img className="rb-glitch-copy rb-glitch-a" src="/assets/game-cover.jpg" alt="" aria-hidden="true" />
          <img className="rb-glitch-copy rb-glitch-b" src="/assets/game-cover.jpg" alt="" aria-hidden="true" />
          <span className="game-ad-veil">ENTER TRAINING SIM</span>
        </Link>
        <Link to="/riotball" className="game-ad-ticker" data-testid="game-ad-results-ticker" aria-label="Latest AL Riotball league results — enter the arena">
          <span className="arena-ticker-label">LATEST RESULTS //</span>
          <div className="arena-ticker-viewport">
            {highlights.length ? (
              <div className="arena-ticker-track">
                {[...highlights, ...highlights].map((h, i) => (
                  <span key={i} className={`arena-ticker-item ${h.team === "red" ? "score-red" : "score-white"}`}>
                    {h.team === "red" ? "RED FORCE" : "WHITE GUARD"} {h.score} — NEW LEAGUE RECORD · {timeAgo(h.at)}
                  </span>
                ))}
              </div>
            ) : (
              <span className="arena-ticker-item">NO RECORDS YET — ENTER THE ARENA AND SET ONE</span>
            )}
          </div>
        </Link>
      </Reveal>
    </section>
  );
}
